/* MakroDirect.jsx — paste product images (Ctrl+V), set qty, send PDF
 * to the Makro rep on WhatsApp.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShoppingBag, Plus, X, FileDown, MessageCircle, Settings, Clipboard, Trash2, Send,
} from "lucide-react";
import { toast } from "sonner";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Resize + compress an image File/Blob to JPEG ≤ 800px on the long side.
const resizeImageToDataUrl = (blob, maxSide = 800, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export default function MakroDirect() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTimer, setSavingTimer] = useState(null);
  const [repSettings, setRepSettings] = useState({ phone: "", name: "" });
  const [showRepSettings, setShowRepSettings] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  const loadOrders = useCallback(async () => {
    const { data } = await axios.get(`${API}/makro-orders`);
    setOrders(data || []);
  }, []);

  const loadOrder = useCallback(async (id) => {
    if (!id) {
      setOrder({ id: null, name: `Makro Order ${new Date().toLocaleDateString("en-ZA", { day: "2-digit", month: "short" })}`, status: "draft", items: [] });
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/makro-orders/${id}`);
      setOrder(data);
    } catch (e) {
      toast.error("Order not found");
      navigate("/makro-direct");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadOrders();
    axios.get(`${API}/makro-orders/_settings/rep`).then(({ data }) => setRepSettings(data || {}));
  }, [loadOrders]);

  useEffect(() => {
    loadOrder(orderId);
  }, [orderId, loadOrder]);

  // Save (create or update) — debounced.
  const persistOrder = useCallback(async (newOrder) => {
    try {
      const payload = {
        id: newOrder.id,
        name: newOrder.name,
        status: newOrder.status,
        items: newOrder.items,
      };
      const { data } = await saveWithRetry(() => axios.post(`${API}/makro-orders`, payload));
      // If this was a fresh order, capture the new id from the server.
      if (!newOrder.id) {
        navigate(`/makro-direct/${data.id}`, { replace: true });
      }
      setOrder(data);
      loadOrders();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save order"));
    }
  }, [navigate, loadOrders]);

  const queueSave = useCallback((nextOrder) => {
    setOrder(nextOrder);
    if (savingTimer) clearTimeout(savingTimer);
    const t = setTimeout(() => persistOrder(nextOrder), 700);
    setSavingTimer(t);
  }, [persistOrder, savingTimer]);

  const addImageItems = useCallback(async (blobs) => {
    if (!order) return;
    const newItems = [];
    for (const b of blobs) {
      if (!b || !b.type?.startsWith("image/")) continue;
      try {
        const dataUrl = await resizeImageToDataUrl(b);
        newItems.push({ id: crypto.randomUUID(), image_data_url: dataUrl, quantity: 1, note: "" });
      } catch (e) {
        console.error("resize failed", e);
      }
    }
    if (newItems.length === 0) return;
    const next = { ...order, items: [...(order.items || []), ...newItems] };
    queueSave(next);
    toast.success(`${newItems.length} item${newItems.length > 1 ? "s" : ""} added`);
  }, [order, queueSave]);

  // Global paste handler — Ctrl+V anywhere on the page adds an item.
  useEffect(() => {
    const handler = async (e) => {
      const items = e.clipboardData?.items || [];
      const blobs = [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const blob = it.getAsFile();
          if (blob) blobs.push(blob);
        }
      }
      if (blobs.length > 0) {
        e.preventDefault();
        await addImageItems(blobs);
      }
    };
    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
  }, [addImageItems]);

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) await addImageItems(files);
    e.target.value = "";
  };

  const handleQtyChange = (itemId, newQty) => {
    if (!order) return;
    const next = {
      ...order,
      items: order.items.map(i => i.id === itemId ? { ...i, quantity: Math.max(0, parseFloat(newQty) || 0) } : i),
    };
    queueSave(next);
  };

  const handleNoteChange = (itemId, newNote) => {
    if (!order) return;
    const next = {
      ...order,
      items: order.items.map(i => i.id === itemId ? { ...i, note: newNote } : i),
    };
    queueSave(next);
  };

  const handleRemoveItem = (itemId) => {
    if (!order) return;
    const next = { ...order, items: order.items.filter(i => i.id !== itemId) };
    queueSave(next);
  };

  const handleRenameOrder = (newName) => {
    if (!order) return;
    queueSave({ ...order, name: newName });
  };

  const handleSendWhatsApp = async () => {
    if (!order?.id) { toast.error("Save the order first"); return; }
    if ((order.items || []).length === 0) { toast.error("Add at least one item"); return; }
    if (!repSettings.phone) {
      toast.error("Set the Makro rep WhatsApp number first");
      setShowRepSettings(true);
      return;
    }
    // Mark as sent and open WhatsApp Web.
    try {
      await axios.post(`${API}/makro-orders`, { id: order.id, status: "sent" });
      const phone = repSettings.phone.replace(/[^\d+]/g, "").replace(/^\+/, "");
      const totalQty = order.items.reduce((s, i) => s + (i.quantity || 0), 0);
      const text = encodeURIComponent(
        `Hi${repSettings.name ? " " + repSettings.name : ""}! Please capture this Makro Direct order — ${order.items.length} item${order.items.length !== 1 ? "s" : ""}, total qty ${totalQty}. PDF attached below. ` +
        `Reference: ${order.name}.`
      );
      // Open WhatsApp Web with pre-filled text. User attaches PDF manually.
      window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
      // Auto-download PDF so user can drag it into WhatsApp.
      window.open(`${BACKEND}/api/makro-orders/${order.id}/pdf`, "_blank");
      toast.success("WhatsApp opened. Attach the downloaded PDF in the chat.");
      loadOrder(order.id);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to send"));
    }
  };

  const handleDownloadPdf = () => {
    if (!order?.id) return;
    window.open(`${BACKEND}/api/makro-orders/${order.id}/pdf`, "_blank");
  };

  const handleNewOrder = () => navigate("/makro-direct");
  const handleSwitchOrder = (id) => navigate(`/makro-direct/${id}`);

  const handleSaveRep = async () => {
    try {
      await axios.put(`${API}/makro-orders/_settings/rep`, repSettings);
      toast.success("Rep details saved");
      setShowRepSettings(false);
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const handleDeleteOrder = async () => {
    if (!order?.id) return;
    if (!window.confirm(`Delete "${order.name}"?`)) return;
    try {
      await axios.delete(`${API}/makro-orders/${order.id}`);
      toast.success("Deleted");
      navigate("/makro-direct");
      loadOrders();
    } catch (e) { toast.error("Failed to delete"); }
  };

  if (loading || !order) {
    return <div className="p-8 text-center text-gray-500">Loading…</div>;
  }

  const items = order.items || [];

  return (
    <div className="p-6 w-full" data-testid="makro-direct-page">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 flex-1">
          <ShoppingBag className="h-7 w-7 text-blue-600" />
          <div className="flex-1">
            <Input
              value={order.name}
              onChange={(e) => handleRenameOrder(e.target.value)}
              className="text-lg font-semibold border-none px-0 focus:ring-0 shadow-none h-auto py-1 -mt-1"
              data-testid="order-name-input"
            />
            <p className="text-xs text-gray-500">Paste product photos (Ctrl+V) and set qty — then send to the Makro rep on WhatsApp.</p>
          </div>
          <Badge variant={order.status === "sent" ? "default" : "outline"} className={order.status === "sent" ? "bg-green-600" : ""}>
            {order.status === "sent" ? "Sent" : "Draft"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowRepSettings(true)} data-testid="rep-settings-btn">
            <Settings className="h-4 w-4 mr-1" /> Rep
          </Button>
          <Button variant="outline" size="sm" onClick={handleNewOrder} data-testid="new-order-btn">
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <div>
          {/* Paste zone */}
          <Card className="border-dashed border-2 mb-4 bg-blue-50/40 hover:bg-blue-50 transition">
            <CardContent
              className="py-8 text-center cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="paste-zone"
            >
              <Clipboard className="h-10 w-10 mx-auto mb-2 text-blue-500" />
              <p className="font-medium text-gray-700">Press <kbd className="px-1.5 py-0.5 bg-white border rounded text-xs font-mono">Ctrl + V</kbd> anywhere to paste a product photo</p>
              <p className="text-xs text-gray-500 mt-1">or click here to pick from your computer · you can paste multiple images at once</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleFilePick}
                data-testid="image-file-input"
              />
            </CardContent>
          </Card>

          {/* Items grid */}
          {items.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <ShoppingBag className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No items yet. Paste a product photo from the Makro website to start.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {items.map((it, idx) => (
                <Card key={it.id} className="overflow-hidden group" data-testid={`item-card-${idx}`}>
                  <button
                    onClick={() => setPreviewImage(it.image_data_url)}
                    className="block w-full bg-white border-b"
                    data-testid={`item-image-${idx}`}
                  >
                    <img
                      src={it.image_data_url}
                      alt={`Item ${idx + 1}`}
                      className="w-full aspect-square object-contain p-2"
                    />
                  </button>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-[10px]">Item {idx + 1}</Badge>
                      <button
                        onClick={() => handleRemoveItem(it.id)}
                        className="opacity-0 group-hover:opacity-100 transition text-red-500 hover:text-red-700"
                        data-testid={`remove-item-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wide">Qty</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={it.quantity}
                        onChange={(e) => handleQtyChange(it.id, e.target.value)}
                        className="font-bold text-lg text-center h-10"
                        data-testid={`item-qty-${idx}`}
                      />
                    </div>
                    <Input
                      placeholder="Note (optional)…"
                      value={it.note || ""}
                      onChange={(e) => handleNoteChange(it.id, e.target.value)}
                      className="text-xs h-8"
                      data-testid={`item-note-${idx}`}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Action bar */}
          {items.length > 0 && (
            <div className="mt-4 flex items-center justify-between gap-2 flex-wrap sticky bottom-2 z-30 bg-white p-3 rounded-lg shadow-lg border">
              <div className="text-sm text-gray-600">
                <strong className="text-gray-900">{items.length}</strong> item{items.length !== 1 ? "s" : ""}
                <span className="mx-2">·</span>
                Total qty <strong className="text-gray-900">{items.reduce((s, i) => s + (i.quantity || 0), 0)}</strong>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={handleDeleteOrder} data-testid="delete-order-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete order
                </Button>
                <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
                  <FileDown className="h-4 w-4 mr-1" /> Download PDF
                </Button>
                <Button onClick={handleSendWhatsApp} className="bg-green-600 hover:bg-green-700" data-testid="send-whatsapp-btn">
                  <Send className="h-4 w-4 mr-1" /> Send via WhatsApp
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar: recent orders */}
        <Card>
          <CardHeader className="border-b pb-2 pt-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Recent orders</CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[600px] overflow-y-auto">
            {orders.length === 0 ? (
              <p className="text-xs text-gray-500 p-3 text-center">No saved orders yet.</p>
            ) : (
              orders.map(o => (
                <button
                  key={o.id}
                  onClick={() => handleSwitchOrder(o.id)}
                  className={`block w-full text-left px-3 py-2 border-b last:border-0 hover:bg-blue-50 transition ${o.id === order.id ? "bg-blue-50" : ""}`}
                  data-testid={`sidebar-order-${o.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 truncate">
                      <div className="font-medium text-sm truncate">{o.name}</div>
                      <div className="text-xs text-gray-500">{o.item_count} item · qty {o.total_qty}</div>
                    </div>
                    {o.status === "sent" ? (
                      <Badge className="bg-green-600 text-[10px]">Sent</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Draft</Badge>
                    )}
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Image preview dialog */}
      <Dialog open={!!previewImage} onOpenChange={(o) => !o && setPreviewImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
          </DialogHeader>
          {previewImage && <img src={previewImage} alt="Preview" className="w-full max-h-[70vh] object-contain" />}
        </DialogContent>
      </Dialog>

      {/* Rep settings */}
      <Dialog open={showRepSettings} onOpenChange={setShowRepSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Makro Rep WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="rep-name">Rep name (optional)</Label>
              <Input id="rep-name" value={repSettings.name} onChange={(e) => setRepSettings(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Sipho" />
            </div>
            <div>
              <Label htmlFor="rep-phone">WhatsApp number (with country code)</Label>
              <Input id="rep-phone" value={repSettings.phone} onChange={(e) => setRepSettings(p => ({ ...p, phone: e.target.value }))} placeholder="+27 82 123 4567" />
              <p className="text-xs text-gray-500 mt-1">Include country code (+27 for SA). Used to open WhatsApp Web with the order PDF.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveRep}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
